import { ItemRelationshipEntityType } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { QueryConfig } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import { ItemEntity } from '@/types/api';

export const getItem = async (
  orgId: string,
  projectId: string,
  itemId: string,
  relationships?: ItemRelationshipEntityType[],
): Promise<ItemEntity> => {
  const { data } = await api.get<undefined, ApiResponse<ItemEntity>>(
    `/orgs/${orgId}/projects/${projectId}/items/${itemId}`,
    {
      params: relationships ? { relationships } : undefined,
    },
  );
  return data;
};

export const getItemQueryKey = (
  orgId?: string | null,
  projectId?: string | null,
  itemId?: string | null,
  relationships?: ItemRelationshipEntityType[],
) => {
  const queryKey: unknown[] = ['item'];
  if (orgId) queryKey.push('org', orgId);
  if (projectId) queryKey.push('project', projectId);
  if (itemId) queryKey.push('item', itemId);
  if (relationships) queryKey.push('relationships', relationships);
  return queryKey;
};

export const getItemQueryOptions = (
  orgId: string | undefined | null,
  projectId: string | undefined | null,
  itemId: string | undefined | null,
  relationships?: ItemRelationshipEntityType[],
) => {
  return queryOptions({
    queryKey: getItemQueryKey(orgId, projectId, itemId, relationships),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => getItem(orgId!, projectId!, itemId!, relationships),
    enabled: !!orgId && !!projectId && !!itemId,
  });
};

interface UseItemOptions {
  queryConfig?: QueryConfig<typeof getItemQueryOptions>;
}

export const useItem = (itemId: string | undefined, { queryConfig }: UseItemOptions = {}) => {
  const { orgId, projectId } = useScopeIds('org', 'project');
  return useQuery({
    ...getItemQueryOptions(orgId, projectId, itemId),
    ...queryConfig,
  });
};
